import React, {createContext, useContext, useMemo, useState} from 'react';

import {serverFlagSystemMetricsQueryLimit} from '../../../config';
import {usePanelConfigContext} from '../PanelConfigContext';
import {isSystemMetric} from './../../../util/metrics';

type PanelSystemMetricsContextType = {
  allowUncappedMetrics: boolean; // if a user has clicked "load anyway"
  isDelayedSystemMetrics: boolean; // if the panel should be delayed
  isSystemMetricsPanel: boolean; // if the panel is a system metrics panel
  shouldCapSystemMetrics: boolean; // if we should throttle the number of points in the system metrics queries
  setAllowUncappedMetrics: (allowUncappedMetrics: boolean) => void; // allow a user to force the rendering
};

export const PanelSystemMetricsContext =
  createContext<PanelSystemMetricsContextType>({
    allowUncappedMetrics: false,
    isDelayedSystemMetrics: false,
    isSystemMetricsPanel: false,
    shouldCapSystemMetrics: false,
    setAllowUncappedMetrics: () => {},
  });

export const PanelSystemsMetricsProvider = ({
  children,
  metrics,
}: {
  children: React.ReactNode;
  metrics: string[];
}) => {
  const {limit: numRuns} = usePanelConfigContext();
  const [allowUncappedMetrics, setAllowUncappedMetrics] = useState(false);

  // this is a fast follow - but not needed for launch
  const [shouldCapSystemMetrics] = useState(false);

  const isSystemMetricsPanel = metrics.every(isSystemMetric);

  /**
   * This is an arbitrary value (roughly the max number of runs possible in sampling mode * 2). It is likely to be a moving target in the near term as we calibrate this with platform.
   */
  const queryLimit = serverFlagSystemMetricsQueryLimit();
  const hitsCapLimit = numRuns * metrics.length > queryLimit;

  // Conditions for display:
  // 1. This only applies to system metric panels - not regular metrics
  // 2. The panel query fanout (numMetrics * numRuns) exceeds our threshold
  // 3. The user has not manually overridden the cap by clicking "load anyway"
  const isDelayedSystemMetrics =
    isSystemMetricsPanel && hitsCapLimit && !allowUncappedMetrics;

  const value = useMemo(
    () => ({
      allowUncappedMetrics,
      isDelayedSystemMetrics,
      isSystemMetricsPanel,
      shouldCapSystemMetrics,
      setAllowUncappedMetrics,
    }),
    [
      allowUncappedMetrics,
      isDelayedSystemMetrics,
      isSystemMetricsPanel,
      shouldCapSystemMetrics,
    ]
  );

  return (
    <PanelSystemMetricsContext.Provider value={value}>
      {children}
    </PanelSystemMetricsContext.Provider>
  );
};

export const usePanelSystemMetricsContext = () => {
  const context = useContext(PanelSystemMetricsContext);
  if (!context) {
    console.error(
      'usePanelSystemMetricsContext must be used within a PanelSystemMetricsProvider'
    );
    return {
      allowUncappedMetrics: true,
      isDelayedSystemMetrics: false,
      isSystemMetricsPanel: false,
      setAllowUncappedMetrics: () => {},
      shouldCapSystemMetrics: false,
    };
  }
  return context;
};
